import {
  BottomSheet,
  BottomSheetExtendedProps,
  StackNavigationProp,
  useNavigation,
} from '@bonanza-social/navigation';
import { Platform } from 'react-native';
import { useCallback, useMemo, useState } from 'react';
import usePurchaseEligibilityCheck from './usePurchaseEligibilityCheck';
import { useDispatch } from '@bonanza-social/toolkit/state';
import {
  openShopRewardsBottomSheet,
  purchaseShopItem,
  purchaseShopSlot,
} from '../state';
import { ShopItemRewardType } from '../state/items/items.types';

type UsePurchaseShopItemParams = {
  id: number;
  type: ShopItemRewardType;
  isSlotItem?: boolean;
  closeBottomSheet?: BottomSheetExtendedProps['close'];
};

export const usePurchaseShopItem = ({
  id,
  type,
  isSlotItem = false,
  closeBottomSheet,
}: UsePurchaseShopItemParams) => {
  const [isPurchasing, setIsPurchasing] = useState(false);
  const openErrorBottomSheet = usePurchaseEligibilityCheck();
  const dispatch = useDispatch();
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();

  const purchase = useCallback(async () => {
    if (openErrorBottomSheet) {
      return openErrorBottomSheet();
    }

    setIsPurchasing(true);
    const action = isSlotItem ? purchaseShopSlot : purchaseShopItem;
    const response = await dispatch(action(id));

    if (action.fulfilled.match(response)) {
      const validResponse = response.payload;
      const isWeb = Platform.OS === 'web';

      if (type === 'MysteryBox') {
        navigation.navigate(
          'ShopMysteryBox',
          isWeb
            ? {
              options: {
                onClosed: () => {
                  dispatch(openShopRewardsBottomSheet(validResponse));
                },
              },
            }
            : {},
        );

        // Open the rewards bottom sheet after the mystery box modal is closed (focus is returned to the shop screen. RN only)
        const unsubscribe = navigation.addListener('focus', () => {
          dispatch(openShopRewardsBottomSheet(validResponse));
          unsubscribe();
        });
      } else if (type === 'Physical') {
        BottomSheet.open({
          namespace: 'common',
          type: 'Info',
          titleNS: 'shop',
          titleKey: 'shopPhysicalItemTitle',
          data: {
            textNS: 'shop',
            textKey: 'shopPhysicalItemMessage',
          },
        });
      } else {
        dispatch(openShopRewardsBottomSheet(validResponse));
      }
    }

    setIsPurchasing(false);

    if (closeBottomSheet) {
      closeBottomSheet();
    }
  }, [
    dispatch,
    openErrorBottomSheet,
    navigation,
    type,
    id,
    isSlotItem,
    closeBottomSheet,
  ]);

  return useMemo(
    () => ({
      purchase,
      isPurchasing,
    }),
    [purchase, isPurchasing],
  );
};

export default usePurchaseShopItem;
